import { UnknownObject } from '../../../../react-app-env';
import { v4 as uuidv4 } from 'uuid';
import { capitaliseFirstOnly, numericToAlpha } from '../../../../utils/stringUtils';

export enum EnumSampleTypes {
  POT = 'POT',
  TRANSFUSION_BAG = 'TRANSFUSION_BAG',
  BLOODY_VIAL = 'BLOODY_VIAL',
  BLOCK = 'BLOCK',
  SLIDE = 'SLIDE',
  WET_TISSUE = 'WET_TISSUE',
}

export const enumSampleTypeDescriptions: { [key: string]: string } = {
  [EnumSampleTypes.POT]: 'Pot',
  [EnumSampleTypes.TRANSFUSION_BAG]: 'Transfusion Bag',
  [EnumSampleTypes.BLOODY_VIAL]: 'Blood Vial',
  [EnumSampleTypes.BLOCK]: 'Block',
  [EnumSampleTypes.SLIDE]: 'Slide',
  [EnumSampleTypes.WET_TISSUE]: 'Wet Tissue',
};

export interface Pots {
  pots: Pot[];
}

export interface Pot {
  id: string;
  name: string;
  pathway: UnknownObject;
  notes: string;
  selected: boolean;
  speciality_category_id: string;
  sample_type: string;
}

export interface ValidationErrors {
  no_name: string | boolean;
  incomplete: string | boolean;
}

export const validationErrors: ValidationErrors = {
  no_name: 'Please provide a name for the pot',
  incomplete: 'Please complete all required * fields',
};

export enum PotsReducerAction {
  ADD_POT,
  NAME,
  NOTES,
  UPDATE_POT,
  SELECT_POT,
  DELETE_POT,
  INIT,
}

const recalculatePotNames = (pots: Pot[]): Pot[] => {
  const categoryOrder = Array.from(new Set(pots.map((p) => p.speciality_category_id)));
  const counterMap = new Map<string, number>();

  return pots.map((pot) => {
    const catIndex = categoryOrder.indexOf(pot.speciality_category_id);
    const prefix = numericToAlpha(catIndex);
    const count = (counterMap.get(pot.speciality_category_id) || 0) + 1;
    counterMap.set(pot.speciality_category_id, count);

    return {
      ...pot,
      name: `Sample ${prefix}${count}`,
    };
  });
};

export const potsReducer = (state: Pots, action: { type: PotsReducerAction; value: any }): Pots => {
  switch (action.type) {
    case PotsReducerAction.ADD_POT: {
      const newCatId = action.value.speciality_category_id;

      const updatedPots = [
        ...state.pots.map((p) => ({ ...p, selected: false })),
        {
          id: uuidv4(),
          name: '',
          pathway: {},
          selected: true,
          notes: '',
          sample_type: '',
          speciality_category_id: newCatId,
        },
      ];

      return { ...state, pots: recalculatePotNames(updatedPots) };
    }
    case PotsReducerAction.SELECT_POT:
      return {
        ...state,
        pots: state.pots.map((i) => ({ ...i, selected: i.id === action.value.id })),
      };
    case PotsReducerAction.DELETE_POT: {
      const updatedPots = action.value.id
        ? state.pots.filter((i) => i.id !== action.value.id)
        : state.pots.filter((i) => i.speciality_category_id !== action.value.speciality_category_id);
      return { ...state, pots: recalculatePotNames(updatedPots) };
    }
    case PotsReducerAction.NAME:
      const pIndex = state.pots.findIndex((i) => i.id === action.value.id);
      if (pIndex >= 0) {
        const pots = [...state.pots];
        pots[pIndex].name = capitaliseFirstOnly(action.value.name);
        return { ...state, pots };
      }
      return state;
    case PotsReducerAction.NOTES:
      const pIndx = state.pots.findIndex((i) => i.id === action.value.id);
      if (pIndx >= 0) {
        const pots = [...state.pots];
        pots[pIndx].notes = action.value.notes;
        return { ...state, pots };
      }
      return state;
    case PotsReducerAction.UPDATE_POT:
      const potIndex = state.pots.findIndex((i) => i.id === action.value.id);
      if (potIndex >= 0) {
        const pots = [...state.pots];
        pots[potIndex] = action.value;
        return { ...state, pots };
      }
      return state;
    case PotsReducerAction.INIT:
      const potsInit = action.value;
      const pots: Pot[] = [];
      potsInit.forEach((pot: UnknownObject) => {
        const { id, name, pathway, selected = false, notes, speciality_category_id, sample_type } = pot;
        pots.push({ id, name, pathway, selected, notes, speciality_category_id, sample_type });
      });
      return { pots };
    default:
      throw new Error();
  }
};
